import axios from "axios";
import { downloadPdfFile, uploadFile } from "../../store/api/fileUpload";
import store from "../../store/store";
import { loggedInUser, UserAuthorizationRules, UserRole, cookieNames, ITEM_STATUS, ITEM_STATUS_LABEL, dateFilters, SUBSCRIPTION_INTERVAL } from "../constants/constants";
import { saveAs } from 'file-saver';
import { deleteCookie, getCookie, isCookieValid, setCookieWithExpiration, updateCookieExpiration, updateCookieValue } from "./cookieManager";
import dayjs from "dayjs";

export function updateUserContext(roles: any, permissions: any, userId: string, partyName: string, status: string, orgPartyRole: string,
    orgPartyIds: string[], orgPartyName: string, authorizationToken: string, newLogin: boolean, partySubscription: any = {}) {
    // Also the fallback used for the subscription cookie below: the `else` branch
    // declares its own `expirationTime`, shadowing this one, so on activity and
    // token refresh the subscription cookie was written with this value. At the
    // old 15 minutes it expired long before the rest of the session.
    let expirationTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours (buyer session)
    if (newLogin) {
        loggedInUser.partyId = userId;
        loggedInUser.roles = roles;
        loggedInUser.permissions = permissions;
        loggedInUser.status = status;
        loggedInUser.orgPartyRole = orgPartyRole;
        loggedInUser.orgPartyIds = orgPartyIds;
        loggedInUser.orgPartyName = orgPartyName;
        loggedInUser.partyName = partyName;
        expirationTime = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours (buyer session)
        // console.log("expirationTime : ", expirationTime);
        const userObject = JSON.stringify(loggedInUser);
        setSessionVariable(cookieNames.prismUser, userObject, expirationTime);
        setSessionVariable(cookieNames.jwtToken, authorizationToken, expirationTime);
        setSessionVariable(cookieNames.tokenExpiryTime, expirationTime.getTime(), expirationTime);
        
    } else {
        // Buyer session is 24 hours (SOCIETYPORTAL). Extend the cookie to 24h on both
        // token refresh and regular activity so the frontend matches the backend JWT.
        const extensionTime = 24 * 60 * 60 * 1000; // 24 hours
        const expirationTime = new Date(Date.now() + extensionTime);

        updateSessionVariableExpiration(cookieNames.prismUser, expirationTime);
        if (authorizationToken && authorizationToken !== "") {
            updateSessionVariableValue(cookieNames.jwtToken, authorizationToken, expirationTime);
            console.log('Token refreshed - extending session by 24 hours');
        } else {
            updateSessionVariableExpiration(cookieNames.jwtToken, expirationTime);
            console.log('User activity - extending session by 24 hours');
        }
        updateSessionVariableValue(cookieNames.tokenExpiryTime, expirationTime.getTime(), expirationTime);
    }

    if (partySubscription) {
        console.log("partySubscription : ", partySubscription);
        setSessionVariable(cookieNames.subscription, JSON.stringify(partySubscription), expirationTime);
    }
}

export function handleUploadFile(file: any) {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject("No file provided");
            return;
        }

        const fd = new FormData();
        fd.append('file', file[0]);

        store.dispatch(uploadFile(fd))
            .unwrap()
            .then((response: any) => {
                resolve(response.data);
            })
            .catch((error: any) => {
                reject(error);
            });
    });

};

export const handleDownload = async () => {
    try {
        //setLoading(true);
        // Replace 'apiEndpoint' with the actual API endpoint that fetches RFQ items details
        const response = await axios.get('apiEndpoint', {
            responseType: 'blob' // Tell Axios to expect a blob (binary data)
        });

        // Extract filename from response headers or set a default filename
        const contentDisposition = response.headers['content-disposition'];
        const filename = contentDisposition ? contentDisposition.split('filename=')[1] : 'RFQ_Items.xlsx';

        // Save the blob as a file using file-saver library
        saveAs(new Blob([response.data]), filename);
    } catch (error) {
        console.error('Error downloading Excel file:', error);
        // Handle error (e.g., show error message to the user)
    } finally {
        //setLoading(false);
    }
};


// Date format - 2024-02-15T00:00:00.000Z
export function convertMongoDate(dateString: any) {
    // Parse the given date string into a Date object
    if (!dateString || dateString === "") {
        return "";
    }
    const dateObject = new Date(dateString);
    // Format the date into DD/MM/YYYY format

    const formattedDate =
        `${dateObject.getFullYear()}-` +
        `${String(dateObject.getMonth() + 1).padStart(2, '0')}-` +
        `${String(dateObject.getDate()).padStart(2, '0')}`
        ;


    return formattedDate
}

export function getSessionVariable(variableName: any) {
    return getCookie(variableName);
}

export function setSessionVariable(variableName: any, variableValue: any, expirationTimeinMin: any) {
    setCookieWithExpiration(variableName, variableValue, expirationTimeinMin);
}

export function updateSessionVariableExpiration(variableName: any, extendedExpirationTimeinMin: any) {
    updateCookieExpiration(variableName, { expires: extendedExpirationTimeinMin });
}

export function updateSessionVariableValue(variableName: any, updatedValue: any, extendedExpirationTimeinMin: any) {
    updateCookieValue(variableName, updatedValue, { expires: extendedExpirationTimeinMin });
}

export function isSessionValid(variableName: any) {
    return isCookieValid(variableName);
}

export function clearSession(variableName: any) {
    return deleteCookie(variableName);
}

export function getUserContext() {
    let userContext: any = "";
    const prismUser = getSessionVariable(cookieNames.prismUser);
    if (prismUser && prismUser != "") {
        try {
            userContext = JSON.parse(prismUser);
        } catch (error) {
            // Stale/malformed stored context (e.g. from an older app version) - clear it
            // and fall back to a logged-out state instead of crashing the whole app.
            console.error("Error parsing stored user context, clearing it:", error);
            clearSession(cookieNames.prismUser);
            userContext = "";
        }
    }
    return userContext;
}

export async function downloadPDFPost(url: string, reqBody: any, pdfName: string) {
    store
        .dispatch(downloadPdfFile({ url, reqBody }))
        .unwrap()
        .then((response) => {
            const downloadURL = response.data.downloadURL;
            const downloadLink = document.createElement("a");
            document.body.appendChild(downloadLink);
            downloadLink.href = downloadURL;
            downloadLink.download = pdfName;
            downloadLink.target = "_blank";
            downloadLink.click();
            document.body.removeChild(downloadLink);
        })
        .catch((error: any) => {
            console.log(error);
        })
};


export const getItemStatusValue = (currentItemStatus: string, totalItemQuantity: number, deliveredQuantity: number, returnedQuantity: number) => {
    if (currentItemStatus === ITEM_STATUS.RETURNED || currentItemStatus === ITEM_STATUS.CANCELLED) {
        return currentItemStatus;
    }

    let itemStatus = "";
    if (returnedQuantity >= totalItemQuantity && deliveredQuantity === 0) {
        itemStatus = ITEM_STATUS.RETURNED;
    }
    else if (deliveredQuantity === totalItemQuantity) {
        itemStatus = ITEM_STATUS.DELIVERED;
    }
    else if (returnedQuantity > 0) {
        itemStatus = ITEM_STATUS.PARTIALLY_RETURNED;
    }
    else if (returnedQuantity === 0 && deliveredQuantity > 0) {
        itemStatus = ITEM_STATUS.PARTIALLY_ACCEPTED;
    }
    return itemStatus;
}

function getMonday(date: any) {
    let day = date.getDay(),
        diff = date.getDate() - day + (day == 0 ? -6 : 1); // adjust when day is sunday
    return new Date(date.setDate(diff));
}

function getSunday(date: any) {
    let day = date.getDay(),
        diff = date.getDate() + (7 - day) - (day == 0 ? 7 : 0); // adjust when day is sunday
    return new Date(date.setDate(diff));
}

export const getDates = (dateSpan: any) => {
    let startDate: any = "";
    let endDate: any = "";
    const present = new Date();
    switch (dateSpan?.type) {
        case dateFilters["TODAY"].value:
            startDate = new Date();
            endDate = new Date();
            break;
        case dateFilters["WEEK"].value:
            startDate = getMonday(new Date());
            endDate = getSunday(new Date());
            break;
        case dateFilters["MONTH"].value:
            console.log(present);
            startDate = new Date(present.getFullYear(), present.getMonth(), 1);
            endDate = new Date(present.getFullYear(), present.getMonth() + 1, 0);
            console.log(startDate.toLocaleString());
            break;
        case dateFilters["YEAR"].value:
            startDate = new Date(present.getFullYear(), 0, 1);
            endDate = new Date(present.getFullYear(), 11, 31);
            break;
        case dateFilters["CUSTOM"].value:
            startDate = dateSpan.startDate;
            endDate = dateSpan.endDate;
            break;
        default:
            startDate = "";
            endDate = "";
    }
    return { startDate, endDate };
};

export const roundOff = (num: number)=>{
    return parseFloat(
        (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2)
      )
}

export const calculateSubscriptionEndDate = (subscriptionStartDate: Date, subscriptionType: string): Date => {
    // Create a new Date object from the start date to avoid mutation
    const endDate = new Date(subscriptionStartDate);

    if(subscriptionType === SUBSCRIPTION_INTERVAL.YEARLY){
        // Add one year to the billing start date
        endDate.setFullYear(endDate.getFullYear() + 1);
        // Handle cases where the resulting year does not have the same day (e.g., billing on Feb 29 in a leap year)
        if (endDate.getMonth() !== subscriptionStartDate.getMonth()) {
            // If the new year does not have the same month/day (Feb 29th to Feb 28th), set it to the last day of the previous month
            endDate.setMonth(endDate.getMonth() - 1);
            endDate.setDate(0); // This will set the date to the last day of the previous month
        }
    }
    else if(subscriptionType === SUBSCRIPTION_INTERVAL.MONTHLY){
        // Add one month to the billing start date
        endDate.setMonth(endDate.getMonth() + 1);
    }
    else if(subscriptionType === SUBSCRIPTION_INTERVAL.QUARTERLY){
        // Add one month to the billing start date
        endDate.setMonth(endDate.getMonth() + 4);
    }

    // Return the calculated billing cycle end date
    return endDate;
};

// Environment helper: returns 'dev' for dev.prismprocure.ai, otherwise 'prod'
export const getEnvName = (): 'dev' | 'prod' => {
    try {
        const host = (typeof window !== 'undefined' && window.location && window.location.hostname) ? window.location.hostname : '';
        if (host === 'dev.prismprocure.ai') {
            return 'dev';
        }
        return 'prod';
    } catch (e) {
        // Default to prod on any unexpected environment (SSR, tests, etc.)
        return 'prod';
    }
};

export function isValidHSN(hsn: string): boolean {
  var regExp = /^[+-]?\d+(\.\d+)?$/g;
  return (hsn!="" && [2,4,6,8].includes(hsn.trim().length) && regExp.test(hsn) )
}

export const parseAndFormatDate = (dateString:any) => {
        const knownFormats = [
            'DD-MM-YYYY',
            'DD/MM/YYYY',
            'YYYY-MM-DD',
            'YYYY-MM-DDTHH:mm:ssZ',
            'YYYY-MM-DDTHH:mm:ss.SSSZ'
        ];

        const parsed = dayjs(dateString, knownFormats, true);
        if (parsed.isValid()) return parsed.format('DD-MM-YYYY');
        // Fall back to a lenient parse for valid inputs not in the known-format list
        // (ISO datetimes, Date objects, etc.). Only if that also fails do we give up —
        // returning '' rather than silently substituting today's date.
        const lenient = dayjs(dateString);
        return lenient.isValid() ? lenient.format('DD-MM-YYYY') : '';
    };

    export const formatAmount = (amount: number | string) => {
    return Number(amount)?.toLocaleString("en-IN", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "INR",
    })
  };

  export const stackResponsiveWidthCalculation = {
    xs: "100%",
    sm: "calc((100% - 1 * 16px) / 2)",
    md: "calc((100% - 2 * 16px) / 3)",
    lg: "calc((100% - 3 * 16px) / 4)",
  };